import React, { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import reportServices from "app/services/automations/report-services";
import { requestErrors } from "app/services/automations/generated-file-services";
import ReportDateRange, { yesterdayIso } from "../ReportDateRange";

const REFRESH_DELAY_MS = 700;

// Vista previa del editor de plantillas: se vuelve a armar sola un momento después de cada cambio. Usa las fechas del
// período sugerido y, si la plantilla no fija el local, uno de prueba; las dos cosas se eligen de nuevo al generarla.
// Como la del reporte rápido, muestra las primeras páginas.
const ReportTemplatePreview = ({ template, definition, stores, periods, ready, earliestDate }) => {
    const rangeOf = (value) => periods.find((period) => period.value === value)?.range || { from: yesterdayIso(), to: yesterdayIso() };
    const [period, setPeriod] = useState(() => rangeOf(template.default_period));
    const [testStoreId, setTestStoreId] = useState(stores[0]?.id || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const lastRequest = useRef(0);

    // Al cambiar el período sugerido, la vista previa pasa a sus fechas.
    useEffect(() => { setPeriod(rangeOf(template.default_period)); }, [template.default_period]); // eslint-disable-line react-hooks/exhaustive-deps

    const storeId = template.store_id || testStoreId;
    const params = template.store_id ? template.params : { ...template.params, device_ids: [] };
    const request = JSON.stringify({ ...params, store_id: storeId, from: period.from, to: period.to });

    // Solo vale la respuesta del último cambio: una anterior que llega tarde se descarta.
    useEffect(() => {
        if (!ready || !storeId) return undefined;
        lastRequest.current += 1;
        const current = lastRequest.current;
        const timer = setTimeout(() => {
            setLoading(true);
            reportServices.preview(template.report_type, JSON.parse(request))
                .then((result) => {
                    if (current !== lastRequest.current) return;
                    setData(result);
                    setError(null);
                })
                .catch((requestError) => {
                    if (current !== lastRequest.current) return;
                    setData(null);
                    setError(requestErrors(requestError, "No se pudo armar la vista previa."));
                })
                .finally(() => { if (current === lastRequest.current) setLoading(false); });
        }, REFRESH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [template.report_type, request, ready, storeId]);

    const { Preview } = definition;
    if (!Preview) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, mt: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h5">Vista previa</Typography>
                        {loading && <CircularProgress size={16} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Se actualiza sola mientras editas. {template.store_id ? "Las fechas son" : "El local y las fechas son"} solo para
                        probarla: se eligen al generarla.
                    </Typography>
                </Box>
                {!template.store_id && (
                    <Autocomplete
                        size="small" options={stores} disableClearable sx={{ width: { xs: "100%", md: 240 } }}
                        value={stores.find((store) => `${store.id}` === `${testStoreId}`) || null}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
                        onChange={(event, value) => setTestStoreId(value?.id || "")}
                        renderInput={(inputParams) => <TextField {...inputParams} label="Local de prueba" />}
                    />
                )}
                <Box sx={{ width: { xs: "100%", md: 340 } }}>
                    <ReportDateRange size="small" from={period.from} to={period.to} minDate={earliestDate}
                                     onChange={(patch) => setPeriod((current) => ({ ...current, ...patch }))} />
                </Box>
            </Stack>

            {!storeId && <Alert severity="info">Todavía no tienes locales con dispositivos.</Alert>}
            {!!storeId && !ready && <Alert severity="info">Completa la configuración del reporte para ver la vista previa.</Alert>}
            {!!storeId && ready && !!error && <Alert severity="warning">{error}</Alert>}
            {!!storeId && ready && !error && !data && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}
            {!!storeId && ready && !error && !!data && (
                <Box sx={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}>
                    <Preview data={data} />
                </Box>
            )}
        </Paper>
    );
};

export default ReportTemplatePreview;
